import axios, { AxiosError } from "axios";
import { showAlertModal } from "../helper/globalAlert";

const loginPageUrls = {
  patient: "/loginpatient",
  clinician: "/loginclinician",
  admin: "/loginadmin",
};

/**
 * All functions that are directly related to session management should go here.
 */

/**
 * The purpose of this function is to abstract away the complexity of refreshing a token
 * when necessary and then retrying the request that failed because of the invalid token
 *
 * This function takes a function to execute as an argument. If that function throws
 * an axios error with a status code of 403 (which stands for an invalid token) it calls,
 * and it clears the session data and redirects the user to the appropriate login page.
 * @param fn- The function to execute
 * @returns The result of the function execution
 * @throws Re-throws any non-403 errors or 403 errors after handling session cleanup
 */
export async function handleSessionExpiration<Type>(
  fn: () => Type,
): Promise<Type> {
  try {
    return await fn();
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const axiosError = err as AxiosError;
      if (axiosError.response?.status === 403) {
        console.log("Session has expired. Redirecting to login...");
        handleSessionExpired();
      }
    }
    throw err;
  }
}

/**
 * Handles session expiration by clearing authentication data and redirecting to login.
 * Shows an alert message to inform the user about the session expiration.
 *
 * @private
 */
function handleSessionExpired(): void {
  console.log("Session expired, clearing authentication data");

  // Clear session data
  sessionStorage.removeItem("auth");
  const userRole = sessionStorage.getItem("usr");
  sessionStorage.removeItem("usr");

  // Show alert to user
  showAlertModal("Bitte melden Sie sich erneut an.");

  // Redirect to appropriate login page
  const loginPageUrl = loginPageUrls[userRole as keyof typeof loginPageUrls];
  if (loginPageUrl) {
    window.location.replace(loginPageUrl);
  } else {
    // Fallback to clinician login if role is unknown
    window.location.replace(loginPageUrls.clinician);
  }
}
